import {
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Divider, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLink,
  BackToTopButton,
  getCancellationPolicyInfo,
  getCancellationPolicyInfoV2,
  HotelAmenities,
  HotelCarousel,
  HotelDescription,
  HotelName,
  HotelOverview,
  HotelShopRoomTypePickerVariant,
  HotelTripAdvisorReviews,
  Icon,
  IconName, isSpendAndGetOffer,
  MobileHotelShopRoomTypePickerPanelSkeleton,
  PageTabNavigations,
  PremiumStaysInfoAccordian,
  RoomPriceAndRewardsDisplayRedesign,
  VoidWindowNotice,
} from "halifax";
import React, { useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { RouteComponentProps } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  CancellationReason,
  CustomerAccountRole,
  LodgingAddressExact,
  LodgingCollectionEnum,
  RestrictedToProductsEnum,
  SelectedTravelOfferScreen,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  FINTECH_HOTEL_UX_UPDATED,
  getExperimentVariant,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  HOTELS_SHOP_IMAGE_MODAL,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
  useExperiments,
  HOTELS_CANCELLATION_POLICY_EXPERIMENT,
} from "../../../../context/experiments";
import { PATH_HOME } from "../../../../utils/paths";
import { RewardsAccountSelection } from "../../../rewards/components";
import {
  TravelWalletDetailsBanner,
  TravelWalletDrawer,
} from "../../../travel-wallet/components";
import { WishlistToggleHeart } from "../../../wishlist";
import { ConnectedWishlistManagementModal } from "../../../wishlist/WishlistManagementModal/container";
import { WishlistSnackbar } from "../../../wishlist/WishlistSnackbar/component";
import { PremierCollectionShopStep } from "../../reducer";
import { CALIFORNIA_BILL_644_CANCELLATION_TEXT } from "../../textConstants";
import { MobileHotelPhotoModal } from "../MobileHotelPhotoModal";
import { MobilePremierCollectionShopRoomTypePickerPanel } from "../MobilePremierCollectionShopRoomTypePickerPanel";
import { MobileRoomPicker } from "../MobileRoomPicker";
import { MobileShopHeader } from "../MobileShopHeader";
import { PremierCollectionShopExpandedMap } from "../PremierCollectionShopExpandedMap";
import { ShopErrorModal } from "../ShopErrorModal";
import { MobileShopConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";
import { parseQueryString } from "../../../availability/utils/parseQueryString";
import { setPremierCollectionShopProgress } from "../../actions/actions";

export interface IMobileShopProps
  extends MobileShopConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: () => void;
  variant?: "default" | "lifestyle-collection";
}

export const MobileShop = (props: IMobileShopProps) => {
  const {
    history,
    lodging,
    premierCollectionShopCallInProgress,
    chosenProduct,
    hasFailed,
    premierCollectionShopStep,
    viewedPremierCollectionDetailsProperties,
    roomInfoProductsType,
    handleReadyToRedirect,
    variant = "default",
    largestEarnAccount,
    nightCount,
    rewardsKey,
    cheapestProduct,
    cancellationSummary,
    isShopCallFromShopDetails,
    shopCallFailureUrl,
    shopCallFromShopDetails,
    isTravelApp,
    setIsTravelApp,
  } = props;
  const dispatch = useDispatch();
  const [
    showExpandedPremierCollectionMap,
    setShowExpandedPremierCollectionMap,
  ] = React.useState(false);
  const [clickedSectionId, setClickedSectionId] = React.useState<string>();
  const [galleryRef, galleryInView] = useInView({ threshold: 1 });
  const [aboutRef, aboutInView] = useInView({ threshold: 1 });
  const [amenitiesRef, amenitiesInView] = useInView({ threshold: 1 });
  const [reviewsRef, reviewsInView] = useInView({ threshold: 1 });
  const [openPhotoModal, setOpenPhotoModal] = React.useState(false);
  // base state is true to handle the case where hasFailed is true from a previous shop call
  const [hasFailedFromShopDetailsBefore, setHasFailedFromShopDetailsBefore] =
    React.useState<boolean>(true);

  const expState = useExperiments();
  const hotelShopImageModal = getExperimentVariant(
    expState.experiments,
    HOTELS_SHOP_IMAGE_MODAL,
  );
  const isHotelShopImageModal = useMemo(
    () => hotelShopImageModal === AVAILABLE,
    [hotelShopImageModal],
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
  );
  const isTravelWalletCreditsExperiment = useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment],
  );

  const isXmodRedesign = getExperimentVariant(
    expState.experiments,
    XMOD_V1_EXPERIMENT,
  ).startsWith(AVAILABLE);

  const globalMobileNavExperimentVariant = getExperimentVariant(
    expState.experiments,
    GLOBAL_MOBILE_NAV_EXPERIMENT,
  );
  const isGlobalMobileNavExperiment = React.useMemo(
    () => globalMobileNavExperimentVariant === AVAILABLE,
    [globalMobileNavExperimentVariant],
  );

  const fintechHotelUpdatedUXExperiment = getExperimentVariant(
    expState.experiments,
    FINTECH_HOTEL_UX_UPDATED,
  );
  const isFintechHotelUpdatedUXExperiment = useMemo(
    () => fintechHotelUpdatedUXExperiment === AVAILABLE,
    [fintechHotelUpdatedUXExperiment],
  );

  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST,
  );
  const isWishlistExperiment = useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment],
  );

  // Initialize wishlist queries when wishlist experiment is enabled
  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  );
  const isCaliforniaBill644Experiment = useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant],
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const includeTaxesAndFeesInTotalPrice =
    lodging?.price?.displayTaxesAndFeesIncluded;

  const roomProductToDisplay = chosenProduct ?? cheapestProduct;

  const cancellationPolicyInfo = chosenProduct?.cancellationPolicyV2
    ? getCancellationPolicyInfoV2(chosenProduct?.cancellationPolicyV2)
    : chosenProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(
        chosenProduct?.cancellationPolicy,
        textConstants.getCfarSecondaryText,
      )
    : null;

  const isCancellationPolicyExperimentAvailable =
    getExperimentVariant(
      expState.experiments,
      HOTELS_CANCELLATION_POLICY_EXPERIMENT,
    ) === AVAILABLE;

  const isCaliforniaHotel = cancellationSummary?.reasons.includes(
    CancellationReason.CaliforniaBill644,
  );

  const shouldShowCancellationPolicy =
    cancellationPolicyInfo &&
    (!isCancellationPolicyExperimentAvailable || isCaliforniaHotel) &&
    cancellationPolicyInfo.className !== "free-cancellation";

  const selectedTab = React.useMemo(() => {
    if (
      galleryInView &&
      (clickedSectionId ? clickedSectionId === "#gallery-section" : true)
    ) {
      return 0;
    }
    if (
      aboutInView &&
      (clickedSectionId ? clickedSectionId === "#about-section" : true)
    ) {
      return 1;
    }
    if (
      amenitiesInView &&
      (clickedSectionId ? clickedSectionId === "#amenities-section" : true)
    ) {
      return 2;
    }
    if (
      reviewsInView &&
      (clickedSectionId ? clickedSectionId === "#reviews-section" : true)
    ) {
      return 3;
    }

    return undefined;
  }, [galleryInView, aboutInView, amenitiesInView, reviewsInView]);
  const scrollToSection = (sectionId: string) => {
    const BANNER_HEIGHT =
      document
        .getElementById("b2bportal-banner-container")
        ?.getBoundingClientRect().height ?? 0;
    const HEADER_HEIGHT =
      document
        .querySelector(".pc-shop-progress-header-container")
        ?.getBoundingClientRect().height ?? 0;
    const MARGIN = 20;
    const yOffset =
      window.pageYOffset - (BANNER_HEIGHT + HEADER_HEIGHT + MARGIN);
    const element = document.getElementById(sectionId);

    const y = (element?.getBoundingClientRect().top ?? 0) + yOffset;
    setClickedSectionId(sectionId);
    setTimeout(() => {
      setClickedSectionId(undefined);
    }, 500);
    window.scrollTo({ top: y, behavior: "smooth" });
  };
  const tabs = [
    { label: "Gallery", onClick: () => scrollToSection("gallery-section") },
    { label: "About", onClick: () => scrollToSection("about-section") },
    { label: "Amenities", onClick: () => scrollToSection("amenities-section") },
    ...(lodging &&
    lodging.lodging?.tripAdvisorReviews &&
    lodging.lodging.tripAdvisorReviews?.comments?.length > 0
      ? [
          {
            label: "Reviews",
            onClick: () => scrollToSection("reviews-section"),
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    // removes medallia from the fixed bottom that was covering pricing
    if (document && document.getElementById("nebula_div_btn")) {
      document!.getElementById("nebula_div_btn")!.style.display = "none";
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [document.getElementById("nebula_div_btn")]);

  // if a /shop call that originated from the /shop-details page failed, the user should be redirected back
  useEffect(() => {
    if (
      hasFailed &&
      isShopCallFromShopDetails &&
      !hasFailedFromShopDetailsBefore
    ) {
      shopCallFailureUrl
        ? history.push(shopCallFailureUrl)
        : history.push(PATH_HOME);
      shopCallFromShopDetails(false, "");
      setHasFailedFromShopDetailsBefore(true);
    }
  }, [
    hasFailed,
    isShopCallFromShopDetails,
    hasFailedFromShopDetailsBefore,
    shopCallFailureUrl,
    history,
  ]);

  // if the shop call has failed previously hasFailed will be true. this is needed to ensure we only redirect
  // after a new shop call has been attempted
  useEffect(() => {
    if (premierCollectionShopCallInProgress) {
      setHasFailedFromShopDetailsBefore(false);
    }
  }, [premierCollectionShopCallInProgress]);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search],
  );
  const travelApp = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(travelApp.isTravelApp);
  }, [travelApp.isTravelApp]);

  useEffect(() => {
    if (travelApp.isTravelApp) {
      if (parsedQueryString?.shopStep === undefined) {
        // URL missing shopStep so add current Redux state to URL
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("shopStep", premierCollectionShopStep.toString());

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.shopStep !== undefined &&
        parsedQueryString?.shopStep !== premierCollectionShopStep
      ) {
        // URL and Redux state are out of sync so update Redux state to match URL
        // This will happen when the user clicks the back/forward buttons in the browser
        dispatch(setPremierCollectionShopProgress(parsedQueryString?.shopStep));
      }
    }
  }, [
    premierCollectionShopStep,
    parsedQueryString?.shopStep,
    travelApp.isTravelApp,
    history,
  ]);

  const renderRewardsAccount = () => {
    return (
      <Box className="mobile-pc-shop-rewards-account-contents">
        <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
      </Box>
    );
  };

  const renderSkeleton = () => (
    <Box className={"mobile-pc-shop-skeleton"}>
      <Box>
        <HotelName isSkeleton />
      </Box>
      <HotelCarousel isSkeleton />
      <Box className={"section-wrapper"}>
        <HotelOverview isSkeleton />
        <HotelAmenities isSkeleton />
        <HotelOverview isSkeleton />
      </Box>
      <Box className={"section-wrapper"}>
        <HotelAmenities isSkeleton />
        <HotelAmenities isSkeleton />
        <HotelOverview isSkeleton />
      </Box>
      <Box className={"section-wrapper"}>
        <HotelName isSkeleton />
        <HotelCarousel isSkeleton />
      </Box>
      <MobileHotelShopRoomTypePickerPanelSkeleton />
    </Box>
  );

  const renderShop = (premierCollectionShopStep: PremierCollectionShopStep) => {
    const { lodging: lodgingData } = lodging || {};
    return (
      <Box
        className={clsx("mobile-pc-shop-container", {
          "travel-app": isTravelApp,
        })}
      >
        {lodging &&
        premierCollectionShopStep === PremierCollectionShopStep.HotelInfo ? (
          <>
            <MobileShopHeader isVRForPremiumCardHoldersEnabled={false} />

            {renderRewardsAccount()}

            {isHotelShopImageModal && (
              <MobileHotelPhotoModal
                openModal={openPhotoModal}
                setOpenModal={setOpenPhotoModal}
              />
            )}

            <div
              id="gallery-section"
              className="gallery-section"
              onClick={() => {
                if (isHotelShopImageModal) {
                  trackEvent({
                    eventName: "hotel_shop_image_modal",
                    properties: {
                      ...viewedPremierCollectionDetailsProperties,
                      is_pc:
                        lodging?.lodgingCollection ===
                        LodgingCollectionEnum.Premier,
                      is_lc:
                        lodging?.lodgingCollection ===
                        LodgingCollectionEnum.Lifestyle,
                    },
                  });
                  setOpenPhotoModal(true);
                }
              }}
              ref={galleryRef}
            >
              <HotelCarousel media={lodgingData?.media} />
              {isWishlistExperiment && lodging && lodging.lodging.id && (
                <WishlistToggleHeart
                  listingId={lodging.lodging.id}
                  listingImageUrl={lodging.lodging.media?.[0]?.url}
                  stayType={StayType.Hotels}
                  collection={lodging.lodgingCollection}
                />
              )}
            </div>

            <PageTabNavigations
              navItems={tabs}
              selectedTab={selectedTab}
              className={clsx("pc-shop-navigation thin", {
                "travel-app": isTravelApp,
              })}
            />

            {largestEarnAccount && largestEarnAccount.earn.hotelsMultiplier && (
              <Box
                className={clsx("shop-benefits", {
                  "lifestyle-collection": variant === "lifestyle-collection",
                })}
              >
                <Box className="shop-benefits-logo">
                  <Icon
                    name={
                      variant === "lifestyle-collection"
                        ? IconName.LifestyleCollectionHorizontalIcon
                        : IconName.PremierCollectionHorizontalIcon
                    }
                  />
                </Box>

                <Typography className="shop-benefits-list">
                  {textConstants.getBenefitsList(
                    largestEarnAccount.earn.hotelsMultiplier,
                    variant,
                    largestEarnAccount.customerAccountRole ===
                      CustomerAccountRole.Primary,
                    largestEarnAccount.rewardsBalance?.currencyDescription ||
                      largestEarnAccount.rewardsBalance?.currency
                  )}
                </Typography>
                <Box className="additional-benefits">
                  <Icon name={IconName.GiftOutline} />
                  <Typography className="additional-benefits-text">
                    {variant === "lifestyle-collection"
                      ? textConstants.LC_ADDITIONAL_BENEFITS
                      : textConstants.ADDITIONAL_BENEFITS}
                  </Typography>
                </Box>
              </Box>
            )}
            {showFree24HourCancel && <VoidWindowNotice />}
            {!isSpendAndGetOffer(
              lodging?.bestOfferThisLodging?.descriptions
            ) && (
              <TravelWalletDetailsBanner
                offer={lodging?.bestOfferThisLodging}
                showButton={false}
                className="hotel-shop-offer"
                screen={SelectedTravelOfferScreen.PREMIUM_STAYS_DETAILS}
                restrictedToProduct={RestrictedToProductsEnum.Lodging}
                {...props}
              />
            )}

            <div
              className={"section-wrapper description-section"}
              id="about-section"
              ref={aboutRef}
            >
              <Box
                className={clsx("description-wrapper", {
                  "include-taxes-and-fees": includeTaxesAndFeesInTotalPrice,
                })}
              >
                <Typography variant="h2" className="description-title">
                  About the
                  <span className={clsx({ "regular-font": !isXmodRedesign })}>
                    {" "}
                    {lodgingData?.name}
                  </span>
                </Typography>
                {lodgingData && (
                  <PremierCollectionShopExpandedMap
                    lodgingData={lodgingData}
                    showExpandedPremierCollectionMap={
                      showExpandedPremierCollectionMap
                    }
                    setShowExpandedPremierCollectionMap={
                      setShowExpandedPremierCollectionMap
                    }
                    isMobile
                  />
                )}
                <ActionLink
                  className="lodging-address-link"
                  content={
                    <Box className="lodging-address-link-content">
                      <Icon name={IconName.B2BMapPin} />
                      <Typography className="lodging-address">
                        {(lodgingData?.address as LodgingAddressExact).line1}
                      </Typography>
                    </Box>
                  }
                  onClick={() => setShowExpandedPremierCollectionMap(true)}
                />
                {includeTaxesAndFeesInTotalPrice && roomProductToDisplay && (
                    <RoomPriceAndRewardsDisplayRedesign
                      roomProduct={roomProductToDisplay}
                      nightCount={nightCount || 0}
                      rewardsKey={rewardsKey}
                      lodging={lodging}
                      useSlashForPer
                      hasChosenProduct={!!chosenProduct}
                      isMobile
                    />
                  )}
                <Divider className={"description-divider"} />
                <HotelDescription description={lodging?.lodging?.description} />
              </Box>
            </div>
            <div
              className="section-wrapper selected-lodging-amenities-section"
              id="amenities-section"
              ref={amenitiesRef}
            >
              <Box className="left-section">
                <Typography variant="h2" className="amenities-title">
                  {textConstants.AMENITIES_TITLE}
                </Typography>
                <Typography variant="body1" className="amenities-subtitle">
                  {textConstants.AMENITIES_SUBTITLE}
                </Typography>
                <HotelAmenities
                  amenities={lodgingData?.amenities}
                  displayCount={6}
                  hideAmenityIconsInTooltip
                  showAmenitiesText={`View All Amenities (${
                    lodgingData?.amenities && lodgingData?.amenities?.length - 6
                  })`}
                />
                <Box className="additional-benefits">
                  <Icon name={IconName.GiftOutline} />
                  <Typography className="additional-benefits-text">
                    {textConstants.ADDITIONAL_BENEFITS}
                  </Typography>
                </Box>
              </Box>
            </div>

            {lodgingData?.tripAdvisorReviews &&
              lodgingData?.tripAdvisorReviews?.comments?.length > 0 && (
                <div
                  className={clsx("section-wrapper reviews-section")}
                  id="reviews-section"
                  ref={reviewsRef}
                >
                  <Typography variant="h2">{textConstants.REVIEWS}</Typography>
                  <HotelTripAdvisorReviews
                    lodgingData={lodgingData}
                    trim={1000}
                  />
                </div>
              )}
            {shouldShowCancellationPolicy && (
              <Box
                className={clsx(
                  "section-wrapper",
                  "cancellation-policy-section"
                )}
              >
                <Typography
                  variant="h4"
                  className="hotel-shop-cancellation-policy-heading"
                >
                  {cancellationPolicyInfo?.primaryText}
                </Typography>
                <Typography variant="subtitle2">
                  {cancellationPolicyInfo?.secondaryText}
                </Typography>
              </Box>
            )}
            <PremiumStaysInfoAccordian
              variant="only-experience-credit"
              isMobile
              isXmodRedesign={isXmodRedesign}
            />

            {!isGlobalMobileNavExperiment && isTravelWalletCreditsExperiment ? (
              <TravelWalletDrawer />
            ) : undefined}

            <MobilePremierCollectionShopRoomTypePickerPanel />
          </>
        ) : (
          lodging && (
            <MobileRoomPicker
              roomInfoProductsType={roomInfoProductsType}
              handleReadyToRedirect={handleReadyToRedirect}
              isGlobalMobileNavExperiment={isGlobalMobileNavExperiment}
              isVRForPremiumCardHoldersEnabled={false}
              nonRefundablePolicyOverrideText={
                showFree24HourCancel
                  ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                  : undefined
              }
              isFintechHotelUpdatedUxEnabled={isFintechHotelUpdatedUXExperiment}
            />
          )
        )}
        {isGlobalMobileNavExperiment ? (
          <BackToTopButton
            bottom={
              premierCollectionShopStep === PremierCollectionShopStep.HotelInfo
                ? 115
                : undefined
            }
          />
        ) : undefined}
      </Box>
    );
  };
  return !hasFailed ? (
    <Box
      className={clsx("mobile-pc-shop-wrapper", {
        "lifestyle-collection": variant.includes("lifestyle-collection"),
        "global-mobile-nav": isGlobalMobileNavExperiment,
        "travel-app": isTravelApp,
      })}
    >
      {premierCollectionShopCallInProgress
        ? renderSkeleton()
        : renderShop(premierCollectionShopStep)}

      {isWishlistExperiment && (
        <>
          <ConnectedWishlistManagementModal
            history={history}
            entryType="premium_hotel_shop"
            isTravelApp={isTravelApp}
          />
          <WishlistSnackbar />
        </>
      )}
    </Box>
  ) : (
    <ShopErrorModal />
  );
};
